import { NgClass } from '@angular/common';
import { AfterViewInit, Component, DestroyRef, Input, OnInit, signal, untracked } from '@angular/core';
import { Subscription } from 'rxjs';
import { OngoingRequestsService } from 'shared/services';

type LoaderType = 'spinner' | 'overlaidSpinner' | 'overlay' | 'overlayAndSpinner';
type OverlayType = 'dark' | 'light';

@Component({
    selector: 'app-loader',
    templateUrl: './loader.component.html',
    styleUrls: ['./loader.component.scss'],
    imports: [NgClass],
})
export class LoaderComponent implements OnInit, AfterViewInit {
    @Input() type: LoaderType = 'spinner';
    @Input() overlayType: OverlayType = 'dark';
    @Input() set matchSome(value: (string | RegExp)[] | undefined) {
        this._matchSome = LoaderComponent.processMatch(value);
    }

    get matchSome(): (string | RegExp)[] | undefined {
        return this._matchSome;
    }

    @Input() set matchAll(value: (string | RegExp)[] | undefined) {
        this._matchAll = LoaderComponent.processMatch(value);
    }

    get matchAll(): (string | RegExp)[] | undefined {
        return this._matchAll;
    }

    @Input() set matchExcept(value: (string | RegExp)[] | undefined) {
        this._matchExcept = LoaderComponent.processMatch(value);
    }

    get matchExcept(): (string | RegExp)[] | undefined {
        return this._matchExcept;
    }

    @Input() matchAny?: boolean = false; // Shows the loader if any request is ongoing
    @Input() alwaysShow?: boolean = false; // Shows the loader at all times

    private _matchSome?: (string | RegExp)[]; // Shows the loader if at least one of the given strings or regexes matches the current ongoing requests url
    private _matchAll?: (string | RegExp)[]; // Shows the loader if all the given strings or regexes match the current ongoing requests url
    private _matchExcept?: (string | RegExp)[]; // Shows the loader if none of the given strings or regexes match the current ongoing requests url

    private subscriptions: { [key: string]: Subscription } = {};
    public showLoader = signal<boolean | undefined>(false);

    constructor(
        private ongoingRequestsService: OngoingRequestsService,
        private destroyRef: DestroyRef,
    ) {}

    ngOnInit(): void {
        this.subscriptions.getOngoingRequests = this.ongoingRequestsService
            .getOngoingRequests()
            .subscribe(
                ({
                    totalOngoingRequests,
                    requests,
                    requestsWithoutBase,
                    currentlyOngoingRequests,
                    currentlyOngoingRequestsWithoutBase,
                }) => {
                    untracked(() => {
                        const value = this.computeShowLoader(totalOngoingRequests, currentlyOngoingRequestsWithoutBase);
                        this.showLoader.set(value);
                    });
                },
            );
    }

    public ngAfterViewInit(): void {
        if (this.alwaysShow) {
            this.showLoader.set(true);
        }
    }

    private computeShowLoader(totalOngoingRequests: number, allRequestUrls: string[]): boolean {
        if (this.alwaysShow) {
            return true;
        }

        if (this._matchExcept) {
            if (totalOngoingRequests === 0) return false;

            const nonExcludedRequests = allRequestUrls.filter(
                (url) =>
                    !this._matchExcept!.some((excluded) => (typeof excluded === 'object' ? excluded.test(url) : url.includes(excluded))),
            );

            return nonExcludedRequests.length > 0;
        }

        if (this._matchAll) {
            return LoaderComponent.compareUrls(
                allRequestUrls,
                this._matchAll,
                true,
                false,
                (currentUrl: string, comparisonUrl: string | RegExp) => {
                    return (typeof comparisonUrl === 'object' && !comparisonUrl.test(currentUrl)) || currentUrl !== comparisonUrl;
                },
            );
        }

        if (this._matchSome) {
            return LoaderComponent.compareUrls(
                allRequestUrls,
                this._matchSome,
                false,
                true,
                (currentUrl: string, comparisonUrl: string | RegExp) => {
                    return (typeof comparisonUrl === 'object' && comparisonUrl.test(currentUrl)) || currentUrl === comparisonUrl;
                },
            );
        }

        if (this.matchAny) {
            return totalOngoingRequests > 0;
        }

        return true;
    }

    private static compareUrls(
        allRequestUrls: string[],
        urlsToCompareAgainst: (string | RegExp)[],
        defaultReturnValue: boolean,
        comparatorSuccessReturnValue: boolean,
        comparator: Function,
    ): boolean {
        for (let i = 0; i < allRequestUrls.length; i++) {
            const url = allRequestUrls[i];

            for (let j = 0; j < urlsToCompareAgainst.length; j++) {
                const comparisonUrl = urlsToCompareAgainst[j];

                if (comparator(url, comparisonUrl)) {
                    return comparatorSuccessReturnValue;
                }
            }
        }
        return defaultReturnValue;
    }

    private static processMatch(match?: (string | RegExp)[]): (string | RegExp)[] | undefined {
        if (Array.isArray(match)) {
            for (let i = 0; i < match.length; i++) {
                const currentMatch = match[i];

                if (typeof currentMatch === 'string' && currentMatch.startsWith('@Regex:')) {
                    match[i] = new RegExp(`^${currentMatch.substring(7)}$`);
                }
            }
        }
        return match;
    }
}
